import { Loader2 } from "lucide-react";
import { FormProvider } from "react-hook-form";
import TailwindSideModal from "../../../components/commonModal/TailwindSideModal";
import TailwindBottomButtonView from "../../../components/mainBottomButtonView/TailwindBottomButtonView";
import { Skeleton } from "../../../components/ui/common/skeleton";
import CommonSplitContainer from "../../../components/ui/layout/CommonSplitContainer/CommonSplitContainer";
import CompanyFormFields from "../shared/components/CompanyFormFields";
import CompanyLogoUpload from "../shared/components/CompanyLogoUpload";
import LoadingOverlay from "../shared/components/LoadingOverlay";
import { useCompanyEditViewModel } from "./hooks/useCompanyEditViewModel";
import { CompanyEditModalProps } from "./types/companyEditView.types";

/**
 * Tailwind rebuild of the legacy `CompanyEditModal`
 * (src/screens/company/companyEdit/CompanyEditModal.tsx), on the same shell
 * as lcaProduct's `ProductEditModal` / lcaSupplier's `SupplierEditModal`.
 * Shares `CompanyFormFields` with the register drawer; state/submit logic
 * lives in `useCompanyEditViewModel`.
 */
const CompanyEditModal = ({ visibleModal, id, closeModal, onSuccess }: CompanyEditModalProps) => {
  const vm = useCompanyEditViewModel({ visibleModal, id, closeModal, onSuccess });

  return (
    <>
      <TailwindSideModal
        open={visibleModal}
        onOpenChange={open => {
          if (!open) vm.onCancel();
        }}
        eyebrow="Companies"
        title="Edit company"
        titleClassName="tw-font-display"
        description="Set the company identity before assigning its operating details."
      >
        <FormProvider {...vm.methods}>
          <CommonSplitContainer
            currentHeight={0.86}
            overflowX="hidden"
            topChild={
              <div>
                {vm.isLoadingDetails ? (
                  <div className="tw-flex tw-items-center tw-justify-center tw-py-24">
                    <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-primary" />
                  </div>
                ) : (
                  <CompanyFormFields
                    control={vm.methods.control}
                    countries={vm.countries}
                    currencies={vm.currencies}
                    isCountriesLoading={vm.isCountriesLoading}
                    isCurrencyLoading={vm.isCurrencyLoading}
                    logoSlot={
                      vm.isLoadingLogo ? (
                        <Skeleton className="tw-h-36 tw-w-full" />
                      ) : (
                        <CompanyLogoUpload
                          initialImageUrl={vm.logoUrl}
                          onUpload={vm.onLogoSelect}
                          onRemove={vm.onLogoRemove}
                        />
                      )
                    }
                  />
                )}
              </div>
            }
            bottomChild={
              <TailwindBottomButtonView
                cancelButtonStatus
                saveButtonStatus
                saveTitle="Save changes"
                buttonSize="lg"
                saveLoading={vm.isSaving}
                saveDisable={vm.isLoadingDetails || vm.isSaving}
                cancelButton={vm.onCancel}
                saveButton={vm.onSave}
              />
            }
          />
        </FormProvider>
      </TailwindSideModal>

      {vm.isSaving ? <LoadingOverlay /> : null}
    </>
  );
};

export default CompanyEditModal;
